import { buildReport, eventLogText, workerVersionText } from "../diag/collect";
import { copyText, downloadText } from "../diag/clipboard";
import { diagMark } from "../diag/capture";
import { formatBytes } from "../diag/format";
import {
  activeOverrides,
  changeKind,
  ELSEWHERE,
  loadOverrides,
  OVERRIDES,
  saveOverrides,
  sanitizeOverrides,
  urlWithActive,
  type OverrideGroup,
  type Overrides,
} from "../walk/advanced";
import type { KioskSettings } from "../walk/kioskSettings";
import { clearAllWalks, listWalks } from "../walk/storage";

export interface AdvancedHooks {
  openDiagnostics(): void;
  walkOpen(): boolean;
  reloadViewer(): void;
  viewerWindow(): Window | null;
  tablet(): KioskSettings;
  setTablet(key: keyof KioskSettings, on: boolean): void;
  /** The update button's current label and note, as the home footer shows them. */
  updateText(): string;
  checkForUpdates(): Promise<void>;
  updateNow(): Promise<void>;
  savedWalksChanged(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text) el.textContent = text;
  return el;
}

async function cacheEntryCount(): Promise<number> {
  if (!("caches" in window)) return 0;
  let total = 0;
  for (const name of await caches.keys()) total += (await (await caches.open(name)).keys()).length;
  return total;
}

/**
 * "Advanced / Debug": everything normally behind URL parameters as switches (saved per device in
 * `walk-player.advanced`, see src/walk/advanced.ts), plus the reporting and repair tools. A
 * scrollable full-screen sheet, opened from the gear panel, or the home footer. Nothing runs while closed.
 */
export class AdvancedSheet {
  private root: HTMLElement | null = null;
  private status: HTMLElement | null = null;
  private draft: Overrides = {};
  private activeBox: HTMLElement | null = null;
  private lastFocus: Element | null = null;

  constructor(private readonly hooks: AdvancedHooks) {}

  get isOpen(): boolean {
    return this.root !== null;
  }

  open(): void {
    if (this.root) return;
    this.lastFocus = document.activeElement;
    this.draft = loadOverrides();
    const root = node("div", "diag adv");
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-label", "Advanced and debug");
    const bar = node("div", "diag-bar");
    bar.append(node("h2", "", "Advanced / Debug"));
    const close = this.button("Close", () => this.close());
    close.dataset.adv = "close";
    bar.append(close);
    this.status = node("p", "diag-status");
    this.status.setAttribute("role", "status");
    const body = node("div", "diag-body adv-body");
    body.tabIndex = -1;
    body.append(this.reportSection(), this.switchesSection(), this.tabletSection(), this.maintenanceSection(), this.dangerSection());
    root.append(bar, this.status, body);
    this.root = root;
    addEventListener("keydown", this.onKey, true);
    document.body.append(root);
    diagMark("advanced", "opened");
    close.focus();
  }

  close(): void {
    if (!this.root) return;
    removeEventListener("keydown", this.onKey, true);
    this.root.remove();
    this.root = null;
    this.status = null;
    this.activeBox = null;
    if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    this.lastFocus = null;
  }

  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || document.querySelector(".diag:not(.adv)")) return; // Diagnostics on top closes first
    event.stopImmediatePropagation();
    event.preventDefault();
    const confirm = this.root?.querySelector(".adv-confirm");
    if (confirm) confirm.querySelector<HTMLElement>('[data-adv-confirm="no"]')?.click();
    else this.close();
  };

  private say(text: string): void {
    if (this.status) this.status.textContent = text;
  }

  private button(label: string, onClick: () => void, className = ""): HTMLButtonElement {
    const b = node("button", `btn ${className}`.trim(), label);
    b.type = "button";
    b.addEventListener("click", onClick);
    return b;
  }

  private section(title: string, className = ""): HTMLElement {
    const s = node("section", `adv-sec ${className}`.trim());
    s.append(node("h3", "", title));
    return s;
  }

  private actions(...buttons: HTMLElement[]): HTMLElement {
    const row = node("div", "adv-actions");
    row.append(...buttons);
    return row;
  }

  // 1. Reports

  private reportSection(): HTMLElement {
    const s = this.section("Reports");
    const versions = node("pre", "adv-pre", `App v${__APP_VERSION__} · build ${__APP_RELEASED_AT__}\nService worker: asking…`);
    void workerVersionText().then((text) => {
      versions.textContent = `App v${__APP_VERSION__} · build ${__APP_RELEASED_AT__}\nService worker version: ${text}`;
    });
    const share = this.button("Share report", () => void this.shareReport());
    if (typeof navigator.share !== "function") share.hidden = true;
    s.append(
      this.actions(
        this.button("Diagnostics panel", () => this.hooks.openDiagnostics(), "primary"),
        this.button("Copy report", () => void this.copyReport()),
        share,
        this.button("Export event log (.txt)", () => this.exportLog()),
      ),
      this.actions(
        this.button("Copy current URL with active switches", () => void this.copyUrl()),
        this.button("Copy versions", () => void this.say(this.copyVersions(versions))),
      ),
      versions,
    );
    return s;
  }

  private copyVersions(versions: HTMLElement): string {
    void copyText(versions.textContent ?? "");
    return "Versions copied.";
  }

  private async copyReport(): Promise<void> {
    this.say("Collecting the report…");
    const text = await buildReport();
    this.say((await copyText(text)) ? `Report copied (${text.length} characters).` : "Could not copy automatically: use the Diagnostics panel, it shows the text to select.");
  }

  private async shareReport(): Promise<void> {
    this.say("Collecting the report…");
    const text = await buildReport();
    try {
      await navigator.share({ title: "Walk Player diagnostics", text });
      this.say("Shared.");
    } catch (err) {
      this.say((err as Error).name === "AbortError" ? "Share cancelled." : `Could not share: ${(err as Error).message}`);
    }
  }

  private exportLog(): void {
    const name = `walk-player-events-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.txt`;
    this.say(downloadText(name, eventLogText()) ? `Saved ${name} (the browser decides the folder).` : "Could not save the file.");
  }

  private async copyUrl(): Promise<void> {
    const url = urlWithActive(location.origin, location.pathname, loadOverrides(), location.search);
    this.say((await copyText(url)) ? `Copied: ${url}` : url);
  }

  // 2. Switches (URL parameters)

  private switchesSection(): HTMLElement {
    const s = this.section("Switches (normally URL parameters)");
    s.append(
      node("p", "adv-note", "Saved on this device. A real ?parameter in the address still wins for that page. Most need the viewer to load again."),
    );
    const urlParams = new URLSearchParams(location.search);
    const groups = new Map<OverrideGroup, HTMLElement>();
    for (const def of OVERRIDES) {
      let group = groups.get(def.group);
      if (!group) {
        group = node("div", "adv-group");
        group.append(node("h4", "", def.group));
        groups.set(def.group, group);
        s.append(group);
      }
      const row = node("label", "adv-row");
      const text = node("span", "adv-label");
      text.append(node("span", "", def.label), " ", node("code", "adv-param", def.param));
      if (def.hint) text.append(node("small", "adv-hint", def.hint));
      if (urlParams.has(def.param)) text.append(node("small", "adv-hint adv-url", `the address has ?${def.param}=${urlParams.get(def.param)}: it wins on this page`));
      const select = node("select");
      select.dataset.param = def.param;
      for (const option of def.options) {
        const o = node("option", "", option.label);
        o.value = option.value;
        select.append(o);
      }
      select.value = this.draft[def.param] ?? "";
      select.addEventListener("change", () => {
        if (select.value) this.draft[def.param] = select.value;
        else delete this.draft[def.param];
      });
      row.append(text, select);
      group.append(row);
    }
    const apply = this.button(this.hooks.walkOpen() ? "Apply (reloads the viewer)" : "Save (applies when a walk opens)", () => this.apply(), "primary");
    apply.dataset.adv = "apply";
    const reset = this.button("Reset all overrides", () => this.reset());
    reset.dataset.adv = "reset";
    this.activeBox = node("pre", "adv-pre");
    this.activeBox.dataset.adv = "active";
    s.append(this.actions(apply, reset), node("h4", "", "Active overrides"), this.activeBox);
    this.renderActive();
    return s;
  }

  private renderActive(): void {
    if (!this.activeBox) return;
    const rows = activeOverrides(loadOverrides(), location.search);
    this.activeBox.textContent = rows.length
      ? rows.map((r) => `${r.param}=${r.value}   (${r.source === "URL" ? "from the address, wins this page" : "saved on this device"})`).join("\n")
      : "none";
  }

  private apply(): void {
    const previous = loadOverrides();
    const next = sanitizeOverrides(this.draft);
    const saved = saveOverrides(next);
    this.renderActive();
    diagMark("advanced", `overrides ${JSON.stringify(next)}`);
    this.react(changeKind(previous, next), saved, next);
  }

  private reset(): void {
    const previous = loadOverrides();
    this.draft = {};
    saveOverrides({});
    for (const select of this.root?.querySelectorAll<HTMLSelectElement>("select[data-param]") ?? []) select.value = "";
    this.renderActive();
    diagMark("advanced", "overrides reset");
    this.react(changeKind(previous, {}), true, {});
  }

  /** After a change: nothing, a live toggle of the viewer debug panel, or a viewer reload. */
  private react(kind: "none" | "debug-only" | "reload", saved: boolean, next: Overrides): void {
    const note = saved ? "" : " (this browser refused to save it: it only lasts until the page is closed)";
    if (!this.hooks.walkOpen()) {
      this.say(`Saved${note}. It applies the next time a walk opens.`);
    } else if (kind === "none") {
      this.say(`No change${note}.`);
    } else if (kind === "debug-only" && this.toggleDebugLive(next.debug === "1")) {
      this.say(`Viewer debug panel ${next.debug === "1" ? "on" : "off"}${note}.`);
    } else {
      this.hooks.reloadViewer();
      this.close();
    }
  }

  private toggleDebugLive(on: boolean): boolean {
    try {
      const panel = this.hooks.viewerWindow()?.document.querySelector<HTMLElement>("#debug");
      if (!panel) return false;
      panel.hidden = !on;
      return true;
    } catch {
      return false;
    }
  }

  // 3. Tablet switches that already exist

  private tabletSection(): HTMLElement {
    const s = this.section("Switches that exist elsewhere");
    const state = this.hooks.tablet();
    for (const [key, label, param] of [["kiosk", "Kiosk mode", "kiosk"], ["fps", "FPS box", "fps"]] as const) {
      const row = node("label", "adv-row");
      const text = node("span", "adv-label");
      text.append(node("span", "", label), " ", node("code", "adv-param", param));
      const input = node("input");
      input.type = "checkbox";
      input.checked = state[key];
      input.dataset.tablet = key;
      input.addEventListener("change", () => {
        this.hooks.setTablet(key, input.checked);
        this.say(`${label} ${input.checked ? "on" : "off"}.`);
      });
      row.append(text, input);
      s.append(row);
    }
    const list = node("ul", "adv-list");
    for (const item of ELSEWHERE.filter((e) => e.params !== "kiosk, fps")) {
      const li = node("li");
      li.append(node("code", "adv-param", item.params), ` — ${item.where}`);
      list.append(li);
    }
    s.append(list);
    return s;
  }

  // 4. Maintenance

  private maintenanceSection(): HTMLElement {
    const s = this.section("Updates and repair");
    const update = node("p", "adv-note", this.hooks.updateText());
    const walkOpen = this.hooks.walkOpen();
    const reloadViewer = this.button("Reload viewer", () => {
      this.hooks.reloadViewer();
      this.close();
    });
    reloadViewer.disabled = !walkOpen;
    const unregister = this.button("Unregister service worker…", () => void this.unregisterWorker());
    const clear = this.button("Clear offline app cache…", () => void this.clearCaches());
    unregister.disabled = clear.disabled = walkOpen;
    unregister.dataset.adv = "unregister";
    clear.dataset.adv = "clear-cache";
    s.append(
      update,
      this.actions(
        this.button("Check for updates", () => void this.hooks.checkForUpdates().then(() => this.say(this.hooks.updateText()))),
        this.button("Update now", () => void this.hooks.updateNow().then(() => this.say(this.hooks.updateText()))),
      ),
      this.actions(
        reloadViewer,
        this.button("Reload app", () => location.reload()),
        this.button("Re-download offline files", () => void this.redownload()),
      ),
      this.actions(unregister, clear),
    );
    if (walkOpen) s.append(node("p", "adv-note", "Close the walk to unregister the worker or clear its cache."));
    return s;
  }

  private async redownload(): Promise<void> {
    const registration = await navigator.serviceWorker?.getRegistration();
    if (!registration?.active) {
      this.say("No active service worker to ask.");
      return;
    }
    const before = await cacheEntryCount();
    registration.active.postMessage({ type: "CHECK_PRECACHE" });
    this.say("Asked the worker to refill what is missing from the offline copy…");
    for (let i = 0; i < 8; i++) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (!this.root) return;
    }
    const after = await cacheEntryCount();
    this.say(`Cache entries: ${before} before, ${after} after. The worker only refills from the same deployed version, and needs the server.`);
  }

  private async unregisterWorker(): Promise<void> {
    const ok = await this.confirm(
      "Unregister the service worker?\n\nDeleted: the worker registration. The app reloads and registers it again (needs the server), so it works without offline support until then.\n\nNOT deleted: saved walks, settings, switches, the offline app files (Cache Storage).",
      "Unregister",
    );
    if (!ok) return;
    const registrations = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(registrations.map((r) => r.unregister()));
    diagMark("advanced", `unregistered ${registrations.length} worker(s)`);
    this.say(`Unregistered ${registrations.length} worker(s). Reloading…`);
    setTimeout(() => location.reload(), 600);
  }

  private async clearCaches(): Promise<void> {
    const names = "caches" in window ? await caches.keys() : [];
    const ok = await this.confirm(
      `Delete the offline copy of the app (${names.length} cache(s) in Cache Storage)?\n\nDeleted: the downloaded app files. Offline use needs one visit online afterwards.\n\nNOT deleted: saved walks, settings, switches, the service worker registration.`,
      "Delete cache",
    );
    if (!ok) return;
    await Promise.all(names.map((name) => caches.delete(name)));
    diagMark("advanced", `deleted ${names.length} cache(s)`);
    this.say(`Deleted ${names.length} cache(s). Use "Reload app" (online) to download the app again.`);
  }

  // 5. Danger zone

  private dangerSection(): HTMLElement {
    const s = this.section("Danger zone", "adv-danger");
    const del = this.button("Delete ALL saved walks…", () => void this.deleteWalks(), "danger");
    del.dataset.adv = "delete-walks";
    del.disabled = this.hooks.walkOpen();
    s.append(node("p", "adv-note", "Removes every walk saved on this device (the files must be picked again). Settings stay."), this.actions(del));
    if (this.hooks.walkOpen()) s.append(node("p", "adv-note", "Close the walk first."));
    return s;
  }

  private async deleteWalks(): Promise<void> {
    const walks = await listWalks();
    const total = walks.reduce((sum, w) => sum + w.fileSize, 0);
    const ok = await this.confirm(
      `Delete ALL ${walks.length} saved walk(s) (${formatBytes(total)})?\n\nThis cannot be undone: the .walk files must be picked again. Settings, switches and the app itself are not touched.`,
      "Delete all walks",
    );
    if (!ok) return;
    await clearAllWalks();
    diagMark("advanced", `deleted ${walks.length} saved walk(s)`);
    this.hooks.savedWalksChanged();
    this.say(`Deleted ${walks.length} saved walk(s).`);
  }

  /** An in-sheet confirmation (a native confirm() is blocked in some installed apps). */
  private confirm(text: string, yes: string): Promise<boolean> {
    return new Promise((resolve) => {
      const box = node("div", "adv-confirm");
      box.setAttribute("role", "alertdialog");
      const card = node("div", "adv-confirm-card");
      const done = (answer: boolean): void => {
        box.remove();
        resolve(answer);
      };
      const no = this.button("Cancel", () => done(false));
      no.dataset.advConfirm = "no";
      const go = this.button(yes, () => done(true), "danger");
      go.dataset.advConfirm = "yes";
      card.append(node("p", "adv-confirm-text", text), this.actions(no, go));
      box.append(card);
      this.root?.append(box);
      no.focus();
    });
  }
}
